import React, { useState } from "react";
import { LeadCard, Sec, Fold, PendingQuoteLine } from "./parts";
import { quoteView } from "./leadModel.mjs";
import { quoteSourceWords, pendingQuote } from "../../timelineModel.mjs";
import AgreementBuilder, { AGREEMENT_FIELDS } from "./AgreementBuilder";
import { shortDate, plural } from "../format.mjs";
import { PricingRoomStrip } from "../../rooms/RoomStrips";

/*
 * PRICING SENT: what we quoted, verbatim, and what has happened to it (the brief's
 * Pricing sent row; matrix 1e: "Quote verbatim · sent · opened n · their budget ·
 * days quiet · builder"). Undrawn as a frame, so built in the drawn card grammar on
 * Gabriel's ruling (2026-10-01), carrying QuotedPricing.js's rules:
 *
 *   - the terms are the EMAIL's lines, verbatim: his quote is a structure ("$3,500/mo
 *     base", "15% of spend $10K–$50K"), and any single figure this card chose to
 *     print would invent half of it;
 *   - the date is the email's, with the stage's own date beside it only when the
 *     move was approved on another day;
 *   - a later email that suggests new pricing (his revision, their counter)
 *     supersedes the first one only once approved (migration 065): approved, the
 *     card shows its terms with "revised <date>"; waiting, the old terms stay and
 *     a line under them says what is waiting in Approvals;
 *   - "opened n times" only when PandaDoc told us (act.document);
 *   - their budget is labelled THEIRS, with where it came from, because a figure
 *     beside a quote reads as the quote unless it is named;
 *   - with no pricing email on the timeline it says so, and never reaches for
 *     monthly_value as a stand-in.
 * The agreement builder sits collapsed under it, the paper being what usually moves
 * a deal from here to Agreement + legal.
 *
 * Props:
 *   record   the shell's bundle: timeline.rows, deal (dealRows: budget),
 *            view.situation.quiet_days, act (document, facts.stage_entered_at,
 *            pending), company (quoted_terms*)
 *   ops      ops.reload (the builder's saved overrides)
 */
export default function PricingSent({ record, ops }) {
  const [builder, setBuilder] = useState(false);
  const facts = (record.act && record.act.facts) || {};
  const quote = quoteView(record.timeline.rows, { stageAt: facts.stage_entered_at, company: record.company });
  const waiting = pendingQuote(record.act && record.act.pending);
  const budget = (record.deal || []).find((r) => r.key === "budget");
  const doc = record.act && record.act.document;
  const quiet = record.view.situation.quiet_days;
  const d = (ts) => shortDate(ts, { now: record.now });
  const opened = doc && doc.first_viewed_at
    ? Number(doc.views) > 1
      ? `${plural(Number(doc.views), "time")}, last ${d(doc.last_viewed_at)}`
      : `first ${d(doc.first_viewed_at)}`
    : null;

  return (
    <LeadCard label="What we quoted" meta={quiet === null || quiet === undefined ? null : quiet === 0 ? "active today" : `quiet ${plural(quiet, "day")}`}>
      {quote ? (
        <div>
          {quote.intro && <div className="rec-meta">{quote.intro}</div>}
          <div className="rec-headline">{quote.terms[0]}</div>
          {quote.terms.length > 1 && (
            <div className="rec-lead__lines">
              {quote.terms.slice(1).map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          )}
          <div className="rec-sub rec-lead__gap">
            {[quoteSourceWords(quote, d), quote.stage_at ? `stage moved ${d(quote.stage_at)}` : null].filter(Boolean).join(" · ")}
          </div>
          <PendingQuoteLine pending={waiting} d={d} />
          {/* The same quote on the company's prospect room, or the button that puts it there. */}
          <div className="rec-lead__gap"><PricingRoomStrip company={record.company} terms={quote.terms} /></div>
        </div>
      ) : (
        <div>
          <div className="rec-headline">No pricing email on record</div>
          <div className="rec-body">No email on this company’s timeline quotes our pricing, so what went out is not stored anywhere we can read.</div>
          <PendingQuoteLine pending={waiting} d={d} />
        </div>
      )}
      <div className="rec-kv rec-sep">
        <span>Their budget</span>
        <span>
          {budget && !budget.empty ? (
            <>
              {budget.value} · theirs
              {budget.sub ? <span className="rec-meta"> · {budget.sub}</span> : null}
            </>
          ) : (
            <span className="rec-sub">not recorded</span>
          )}
        </span>
        {opened && (
          <>
            <span>Opened</span>
            <span>{opened}</span>
          </>
        )}
      </div>
      <Sec tight>
        <Fold
          title="Agreement builder"
          sub={`.docx from what we quoted · ${AGREEMENT_FIELDS} fields, each in writing, inferred or not in record`}
          open={builder}
          onToggle={() => setBuilder((b) => !b)}
        >
          <AgreementBuilder company={record.company} onChanged={ops.reload} />
        </Fold>
      </Sec>
    </LeadCard>
  );
}
